import {
  type ReportStandardFundForPrivateAnnualDataModel,
  type ReportStandardFundForPrivateModel,
  type ReportStandardFundsForPrivateModel,
  reportStandardFundReportForPrivateAnnualDataCsvHeader,
  reportStandardFundReportForPrivateCsvHeader,
} from '@/features/mold/models/standard-fund';
import {
  BusinessFormLabelModifier,
  CorporateFormLabelModifier,
  FundNecessityTypeLabelModifier,
  OperationFormLabelModifier,
} from '@/features/project/projects/_models/project';
import { isBefore2025 } from '@/features/project/review-sheet/utils/project';
import { t } from '@/i18n';
import {
  CSV_LINE_BREAK,
  escapeForCSV,
  formatAmountForCsv,
  formatAmountReport,
  formatPercentage,
} from '@/utils';
import type { z } from 'zod';

type StandardFundReportForPrivateValueConverterArgs = {
  annualData:
    | z.infer<typeof ReportStandardFundForPrivateAnnualDataModel>
    | undefined;
  type: 'csv' | 'pdf';
  isDebugMode: boolean;
};
export const standardFundReportForPrivateValueConverter = ({
  annualData,
  type,
  isDebugMode,
}: StandardFundReportForPrivateValueConverterArgs) => ({
  totalIncome: formatAmountReport(annualData?.totalIncome, type, {
    isDebugMode,
    negativeCount: annualData?.negativeTotalIncomeCount,
  }),
  totalExpenses: formatAmountReport(annualData?.totalExpenses, type, {
    isDebugMode,
    negativeCount: annualData?.negativeTotalExpensesCount,
  }),
  endingOfTheFundBalance: formatAmountReport(
    annualData?.endingOfTheFundBalance,
    type,
    {
      isDebugMode,
      negativeCount: annualData?.isCanceledEndingOfTheFundBalance ? 1 : 0,
    },
  ),
});

const _getReportTargetFiscalYears = (fiscalYear: number) => {
  const nextFiscalYear = fiscalYear + 1;
  return Array.from(
    { length: nextFiscalYear - FIRST_YEAR_OF_REPORT + 1 },
    (_, i) => i + FIRST_YEAR_OF_REPORT,
  ).slice(-REPORT_YEARS);
};

const FIRST_YEAR_OF_REPORT = 2020;
// TODO: FIRST_YEAR_OF_REPORT is in 2020, so there is no problem for the time being, but the layout needs to be adjusted.
const REPORT_YEARS = 10;

export const getPublicReportTargetYear = (fiscalYear: number) => {
  return fiscalYear - 1;
};

const _getStandardFundReportForPrivateCsvHeader = (
  reportYears: number[],
): Record<
  z.infer<typeof reportStandardFundReportForPrivateCsvHeader>,
  string
> => {
  return {
    fundSheetNumber: t('report-standard-fund-header-fund-sheet-number'),
    ministryName: t('ministry'),
    additionalJurisdictionalMinistries: t('additional-organization-in-charge'),
    fundBusinessName: t('fund-business-name'),
    projectName: t('fund-name'),
    fundOwner: t('fund-owner'),
    corporateForm: t('corporate-form'),
    fiscalYearOfCreation: t('fiscal-year-of-creation'),
    endDate: t('fund-scheduled-end-period'),
    endDateOfNewApplicationReceiving: t('end-date-of-new-application'),
    operationForms: t('operation-form'),
    businessForms: t('business-form'),
    necessityType: t('fund-necessity-type'),
    hasLegalBases: t('report-standard-fund-private-header-has-legal-bases'),
    expenditurePurposes: t('expenditure-budget-name'),
    haveAppliedLawOfAppropriatenessOfSubsidies: t(
      'report-standard-fund-private-header-have-applied-law-of-appropriateness-of-subsidies',
    ),
    beginningOfTheFundBalance: t(
      'report-standard-fund-header-ending-of-the-fund-balance',
      {
        ...(reportYears[0] && { year: reportYears[0] - 1 }),
      },
    ),
    ownershipRatio: t('ownership-ratio'),
  };
};

export const getStandardFundReportForPrivateAnnualDataCsvHeader = (
  year: number,
  isLastOrSecondLastYear: boolean,
): Record<
  z.infer<typeof reportStandardFundReportForPrivateAnnualDataCsvHeader>,
  string
> => {
  return {
    totalIncome: `${t('report-standard-fund-header-total-income', {
      year,
    })}${isLastOrSecondLastYear ? `${t('estimate')}` : ''}`,
    grantedAmountByTheCountryInitial: `${t('fiscal-year-with-value', {
      year,
    })}${t('amount-of-funding-from-the-government')}（${t(
      'report-standard-fund-private-header-granted-amount-by-the-country-initial',
    )}）`,
    grantedAmountByTheCountrySupplementary: `${t('fiscal-year-with-value', {
      year,
    })}${t('amount-of-funding-from-the-government')}（${t(
      'report-standard-fund-private-header-granted-amount-by-the-country-supplementary',
    )}）`,
    grantedAmountByTheCountryReserve: `${t('fiscal-year-with-value', {
      year,
    })}${t('amount-of-funding-from-the-government')}（${t(
      'report-standard-fund-private-header-granted-amount-by-the-country-reserve',
    )}）`,
    grantedAmountByTheCountryAccountingNames: `${t('fiscal-year-with-value', {
      year,
    })}${t('amount-of-funding-from-the-government')}（${t(
      'report-standard-fund-private-header-granted-amount-by-the-country-accounting-names',
    )}）`,
    totalExpenses: `${t('report-standard-fund-header-total-expenses', {
      year,
    })}${isLastOrSecondLastYear ? `${t('estimate')}` : ''}`,
    amountReturnedToTheNationalTreasury: `${t(
      'report-standard-fund-header-amount-returned-to-the-national-treasury',
      { year },
    )}${isLastOrSecondLastYear ? `${t('estimate')}` : ''}`,
    amountReturnedToTheOrganization: `${
      isBefore2025({ fiscalYear: year })
        ? t(
            'report-standard-fund-header-amount-returned-to-the-organization-before-2025',
            { year },
          )
        : t('report-standard-fund-header-amount-returned-to-the-organization', {
            year,
          })
    }${isLastOrSecondLastYear ? `${t('estimate')}` : ''}`,
    endingOfTheFundBalance: `${t(
      'report-standard-fund-header-ending-of-the-fund-balance',
      {
        year,
      },
    )}${isLastOrSecondLastYear ? `${t('estimate')}` : ''}`,
    nationalEquivalentOfTheBalance: t(
      'report-standard-fund-private-header-national-equivalent-of-the-balance',
      {
        year,
      },
    ),
  };
};

export const convertStandardFundReportForPrivateToCsv = ({
  headers,
  annualDataHeaders,
  reports,
  reportTargetFiscalYears,
  isDebugMode,
}: {
  headers: Record<
    z.infer<typeof reportStandardFundReportForPrivateCsvHeader>,
    string
  >;
  annualDataHeaders: Record<
    number,
    Record<
      z.infer<typeof reportStandardFundReportForPrivateAnnualDataCsvHeader>,
      string
    >
  >;
  reports: z.infer<typeof ReportStandardFundsForPrivateModel>;
  reportTargetFiscalYears: number[];
  isDebugMode: boolean;
}) => {
  const itemsAfterAnnualData: Array<
    z.infer<typeof reportStandardFundReportForPrivateCsvHeader>
  > = ['ownershipRatio'];
  const itemsBeforeAnnualData =
    reportStandardFundReportForPrivateCsvHeader.options.filter(
      (option) => !itemsAfterAnnualData.includes(option),
    );
  const itemsBeforeAnnualDataHeaderLabels = itemsBeforeAnnualData.map(
    (value) => `"${headers[value].replace(/\n/g, '')}"`,
  );
  const annualDataHeaderLabels = reportTargetFiscalYears.flatMap((year) => {
    const targetHeaders = annualDataHeaders[year];
    if (!targetHeaders) {
      return [];
    }
    return reportStandardFundReportForPrivateAnnualDataCsvHeader.options.map(
      (value) => `"${targetHeaders[value].replace(/\n/g, '')}"`,
    );
  });
  const itemsAfterAnnualDataHeaderLabels = itemsAfterAnnualData.map(
    (value) => `"${headers[value].replace(/\n/g, '')}"`,
  );

  const str = `${[
    ...itemsBeforeAnnualDataHeaderLabels,
    ...annualDataHeaderLabels,
    ...itemsAfterAnnualDataHeaderLabels,
  ].join(',')}${CSV_LINE_BREAK}`;

  const getCorporateFormText = (
    corporateForm: z.infer<
      typeof ReportStandardFundForPrivateModel
    >['corporateForm'],
    optionalCorporateForm: z.infer<
      typeof ReportStandardFundForPrivateModel
    >['optionalCorporateForm'],
  ): string => {
    if (corporateForm === 'others' && optionalCorporateForm) {
      return optionalCorporateForm;
    }
    return corporateForm ? CorporateFormLabelModifier[corporateForm] : '';
  };

  const csvFormattedReports: Array<
    Record<
      z.infer<typeof reportStandardFundReportForPrivateCsvHeader>,
      string | number | undefined | null
    >
  > = reports.data.map((report) => {
    const last10YearData = report.annualData.find(
      (d) => d.fiscalYear === reportTargetFiscalYears[0],
    );
    return {
      fundSheetNumber: `${report.fundSheetNumber.toString()}${
        report.fundSheetBranchNumber ? `-${report.fundSheetBranchNumber}` : ''
      }`,
      ministryName: report.ministryName,
      additionalJurisdictionalMinistries:
        report.additionalJurisdictionalMinistries?.join('、') ?? '',
      fundBusinessName: report.fundBusinessName,
      projectName: report.projectName,
      fundOwner: report.fundOwner,
      fiscalYearOfCreation: report.fiscalYearOfCreation,
      endDate: report.endDate,
      endDateOfNewApplicationReceiving: report.endDateOfNewApplicationReceiving,
      corporateForm: getCorporateFormText(
        report.corporateForm,
        report.optionalCorporateForm,
      ),
      operationForms: report.operationForms
        ?.map((d) => OperationFormLabelModifier[d])
        .join('、'),
      businessForms: report.businessForms
        ? report.businessForms
            .map((d) => BusinessFormLabelModifier[d])
            .join('、')
        : '',
      necessityType: report.necessityType
        ? FundNecessityTypeLabelModifier[report.necessityType]
        : '',
      hasLegalBases: report.hasLegalBases
        ? t('report-standard-fund-existence-true')
        : t('report-standard-fund-existence-false'),
      expenditurePurposes: report.expenditurePurposes?.map((d) => d).join('、'),
      haveAppliedLawOfAppropriatenessOfSubsidies:
        report.haveAppliedLawOfAppropriatenessOfSubsidies === null
          ? ''
          : report.haveAppliedLawOfAppropriatenessOfSubsidies
            ? t('report-standard-fund-existence-true')
            : t('report-standard-fund-existence-false'),
      beginningOfTheFundBalance: formatAmountForCsv(
        last10YearData?.beginningOfTheFundBalance,
      ),
      ownershipRatio: report.ownershipRatio,
    };
  });

  const csvFormattedAnnualData: Array<
    Record<
      number,
      Record<
        z.infer<typeof reportStandardFundReportForPrivateAnnualDataCsvHeader>,
        string | undefined
      >
    >
  > = reports.data.map((report) =>
    reportTargetFiscalYears.reduce((acc, year) => {
      const yearData = report.annualData.find((d) => d.fiscalYear === year);
      const values = standardFundReportForPrivateValueConverter({
        annualData: yearData,
        type: 'csv',
        isDebugMode,
      });
      acc[year] = {
        totalIncome: values.totalIncome,
        grantedAmountByTheCountryInitial: formatAmountForCsv(
          yearData?.grantedAmountByTheCountryInitial,
        ),
        grantedAmountByTheCountrySupplementary: formatAmountForCsv(
          yearData?.grantedAmountByTheCountrySupplementary,
        ),
        grantedAmountByTheCountryReserve: formatAmountForCsv(
          yearData?.grantedAmountByTheCountryReserve,
        ),
        grantedAmountByTheCountryAccountingNames:
          yearData?.grantedAmountByTheCountryAccountingNames
            ?.map((accountingName) => accountingName)
            .join('、'),
        totalExpenses: values.totalExpenses,
        amountReturnedToTheNationalTreasury: formatAmountForCsv(
          yearData?.amountReturnedToTheNationalTreasury,
        ),
        amountReturnedToTheOrganization: formatAmountForCsv(
          yearData?.amountReturnedToTheOrganization,
        ),
        endingOfTheFundBalance: values.endingOfTheFundBalance,
        nationalEquivalentOfTheBalance: formatAmountForCsv(
          yearData?.nationalEquivalentOfTheBalance,
        ),
      };
      return acc;
    }, {}),
  );

  // NOTE: Because of the annualData map process, createCsv in utils is not used.
  return csvFormattedReports.reduce((str, report, i) => {
    const formattedItemsBeforeAnnualData = itemsBeforeAnnualData.map((value) =>
      escapeForCSV({ value: report[value] ?? '' }),
    );
    const formattedItemsAfterAnnualData = itemsAfterAnnualData.map((value) =>
      escapeForCSV({ value: report[value] ?? '' }),
    );
    const formattedAnnualData = reportTargetFiscalYears.flatMap((year) =>
      reportStandardFundReportForPrivateAnnualDataCsvHeader.options.map(
        (value) =>
          escapeForCSV({
            value: csvFormattedAnnualData[i]?.[year]?.[value] ?? '',
          }),
      ),
    );

    return (
      str +
      formattedItemsBeforeAnnualData
        .concat(formattedAnnualData, formattedItemsAfterAnnualData)
        .join(',') +
      CSV_LINE_BREAK
    );
  }, str);
};
export const formatAdministrativeExpensesRate = (value: number | null) => {
  return formatPercentage(value, {
    maximumFractionDigits: 1,
    useNegativeSign: true,
  });
};
